import { useState } from "react";
import { useTranslations } from "next-intl";
import { motion, AnimatePresence } from "framer-motion";
import { trpc } from "@/lib/trpc";
import { Car, Fuel, Gauge, ArrowRight, Search, X, SlidersHorizontal, ChevronDown, MapPin } from "lucide-react";
import { Link } from "@/i18n/navigation";
import SiteNav from "@/components/SiteNav";

/* ─── Helpers ─── */
const formatPrice = (price: string | null, fallback: string) => {
  if (!price || price === "0") return fallback;
  const n = parseFloat(price);
  return new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(n);
};

const formatMileage = (km: number | null) => {
  if (!km) return "—";
  return new Intl.NumberFormat("fr-CH").format(km) + " km";
};


/* ─── Vehicle Card ─── */
function VehicleCard({ vehicle, index }: { vehicle: any; index: number }) {
  const t = useTranslations("catalogue");
  const tc = useTranslations("common");
  return (
    <motion.a
      href={`/catalogue/${vehicle.id}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className="group block bg-white border border-border hover:border-graphite/20 transition-colors"
    >
      {/* Image */}
      <div className="relative aspect-[16/10] bg-warm-gray overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <Car size={56} className="text-graphite/[0.06]" strokeWidth={1} />
        </div>

        {/* Year badge */}
        <div className="absolute top-3 left-3">
          <span className="text-[10px] font-semibold text-white bg-graphite/70 backdrop-blur-sm px-2 py-1">{vehicle.year}</span>
        </div>

        {/* Hover arrow */}
        <div className="absolute bottom-3 right-3 w-9 h-9 bg-swiss-red flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
          <ArrowRight size={14} className="text-white" />
        </div>
      </div>

      {/* Info */}
      <div className="p-5">
        <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-graphite/30 mb-1">{vehicle.brand}</p>
        <h3 className="text-lg font-bold text-graphite tracking-tight group-hover:text-swiss-red transition-colors" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {vehicle.model}
        </h3>

        {/* Specs */}
        <div className="flex items-center gap-3 mt-3 text-xs text-graphite/40">
          {vehicle.mileage && (
            <span className="flex items-center gap-1">
              <Gauge size={11} />
              {formatMileage(vehicle.mileage)}
            </span>
          )}
          {vehicle.fuelType && (
            <span className="flex items-center gap-1">
              <Fuel size={11} />
              {tc.has(`fuelTypes.${vehicle.fuelType.toLowerCase()}`) ? tc(`fuelTypes.${vehicle.fuelType.toLowerCase()}`) : vehicle.fuelType}
            </span>
          )}
          {vehicle.color && (
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-graphite/15" />
              {vehicle.color}
            </span>
          )}
        </div>

        {/* Price */}
        <div className="mt-4 pt-4 border-t border-border flex items-baseline justify-between">
          <span className="text-xl font-bold text-graphite" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {formatPrice(vehicle.sellingPrice, t("priceOnRequest"))}
          </span>
          <span className="text-[10px] text-graphite/25 uppercase tracking-wider">{t("ttc")}</span>
        </div>
      </div>
    </motion.a>
  );
}

/* ─── Filters ─── */
function Filters({ brands, filters, setFilters }: {
  brands: string[];
  filters: { search: string; brand: string; maxPrice: string; fuelType: string };
  setFilters: (f: any) => void;
}) {
  const t = useTranslations("catalogue");
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-graphite/25" />
        <input
          type="text"
          placeholder={t("searchPlaceholder")}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="w-full bg-white border border-border text-graphite placeholder:text-graphite/30 pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-swiss-red/40 transition-colors"
        />
        {filters.search && (
          <button onClick={() => setFilters({ ...filters, search: "" })} className="absolute right-4 top-1/2 -translate-y-1/2 text-graphite/25 hover:text-graphite">
            <X size={14} />
          </button>
        )}
      </div>

      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-xs font-medium text-graphite/40 hover:text-graphite/60 transition-colors"
      >
        <SlidersHorizontal size={14} />
        {t("advancedFilters")}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <select value={filters.brand} onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
                className="bg-white border border-border text-graphite text-sm px-4 py-2.5 focus:outline-none focus:border-swiss-red/40 appearance-none cursor-pointer">
                <option value="">{t("allBrands")}</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={filters.fuelType} onChange={(e) => setFilters({ ...filters, fuelType: e.target.value })}
                className="bg-white border border-border text-graphite text-sm px-4 py-2.5 focus:outline-none focus:border-swiss-red/40 appearance-none cursor-pointer">
                <option value="">{t("allFuels")}</option>
                <option value="diesel">{t("diesel")}</option>
                <option value="petrol">{t("petrol")}</option>
                <option value="electric">{t("electric")}</option>
                <option value="hybrid">{t("hybrid")}</option>
              </select>
              <select value={filters.maxPrice} onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                className="bg-white border border-border text-graphite text-sm px-4 py-2.5 focus:outline-none focus:border-swiss-red/40 appearance-none cursor-pointer">
                <option value="">{t("maxBudget")}</option>
                <option value="20000">{t("upTo20k")}</option>
                <option value="35000">{t("upTo35k")}</option>
                <option value="50000">{t("upTo50k")}</option>
                <option value="75000">{t("upTo75k")}</option>
                <option value="100000">{t("upTo100k")}</option>
              </select>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Empty state ─── */
function EmptyState({ filtered }: { filtered: boolean }) {
  const t = useTranslations("catalogue");
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-32 text-center">
      <div className="w-20 h-20 bg-warm-gray flex items-center justify-center mb-6 border border-border">
        <Car size={32} className="text-graphite/15" strokeWidth={1} />
      </div>
      <h3 className="text-xl font-bold text-graphite/60 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        {filtered ? t("noResults") : t("emptyTitle")}
      </h3>
      <p className="text-sm text-graphite/35 max-w-md">
        {filtered ? t("noResultsSubtitle") : t("emptySubtitle")}
      </p>
    </motion.div>
  );
}

/* ─── Main ─── */
export default function Catalog() {
  const t = useTranslations("catalogue");
  const tf = useTranslations("footer");
  const { data: vehicles = [], isLoading } = trpc.catalog.list.useQuery();
  const [filters, setFilters] = useState({ search: "", brand: "", maxPrice: "", fuelType: "" });

  const brands = [...new Set(vehicles.map((v) => v.brand))].sort();

  const filtered = vehicles.filter((v) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!`${v.brand} ${v.model} ${v.year} ${v.color ?? ""}`.toLowerCase().includes(q)) return false;
    }
    if (filters.brand && v.brand !== filters.brand) return false;
    if (filters.fuelType && v.fuelType?.toLowerCase() !== filters.fuelType) return false;
    if (filters.maxPrice && v.sellingPrice && parseFloat(v.sellingPrice) > parseInt(filters.maxPrice)) return false;
    return true;
  });

  const isFiltered = filters.search || filters.brand || filters.maxPrice || filters.fuelType;

  return (
    <div className="min-h-screen bg-offwhite">
      <SiteNav />

      {/* Header */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-[1400px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-swiss-red" />
              <span className="text-xs font-semibold tracking-[0.25em] uppercase text-swiss-red">{t("sectionLabel")}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-graphite tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {t("title")} <span className="text-graphite/20">{t("titleAccent")}</span>
            </h1>
            <p className="mt-4 text-graphite/45 max-w-xl text-lg leading-relaxed">
              {t("subtitle")}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-8 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-graphite" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{vehicles.length}</span>
              <span className="text-graphite/35">{vehicles.length !== 1 ? t("vehicleCountPlural") : t("vehicleCount")}</span>
            </div>
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-1.5 text-graphite/35">
              <MapPin size={13} />
              <span>{t("location")}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="px-6 pb-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-10">
            <Filters brands={brands} filters={filters} setFilters={setFilters} />
          </div>

          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse bg-white border border-border">
                  <div className="aspect-[16/10] bg-warm-gray" />
                  <div className="p-5 space-y-3">
                    <div className="h-2 bg-warm-gray w-16" />
                    <div className="h-5 bg-warm-gray w-40" />
                    <div className="h-3 bg-warm-gray w-32" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((vehicle, i) => <VehicleCard key={vehicle.id} vehicle={vehicle} index={i} />)}
            </div>
          )}

          {!isLoading && filtered.length === 0 && <EmptyState filtered={!!isFiltered} />}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-graphite py-16 px-6">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t("ctaTitle")}</h2>
            <p className="mt-2 text-white/40">{t("ctaSubtitle")}</p>
          </div>
          <Link href="/login" className="inline-flex items-center gap-2 bg-swiss-red text-white px-8 py-4 font-semibold text-sm hover:bg-swiss-red/90 transition-colors whitespace-nowrap">
            {t("ctaButton")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-graphite border-t border-white/10 py-8 px-6">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 bg-swiss-red flex items-center justify-center">
              <span className="text-white font-bold text-[10px]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>H</span>
            </div>
            <span className="text-sm font-semibold text-white/50" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Helvetic<span className="text-swiss-red/70">Cars</span>
            </span>
          </div>
          <p className="text-xs text-white/20">{tf("importExport")}</p>
        </div>
      </footer>
    </div>
  );
}
